import React, { useState } from 'react';
import {
  Card,
  Heading,
  Text,
  Button,
  TooltipHoverArea,
} from '@puppet/react-components';
import { useTranslation } from 'react-i18next';
import { UserV1 } from '@puppet/cd4pe-client-ts';
import ConditionalRender from '@components/ConditionalRender';
import ChangeEmailDialog from '../ChangeEmailDialog/ChangeEmailDialog';
import ChangePasswordDialog from '../ChangePasswordDialog/ChangePasswordDialog';

type Props = {
  userToView: UserV1;
  loggedInUser: UserV1;
};

const UserDetailsCard = ({ userToView, loggedInUser }: Props) => {
  const { t } = useTranslation('codeDelivery');
  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const isRootUser = userToView?.id === 1;
  const isLoggedInRootOrSuperUser =
    loggedInUser.rootUser || loggedInUser.superUser;

  return (
    <Card className="user-details-card">
      <div className="user-details-card__details">
        <div className="user-details-card__item">
          <Heading label color="medium">
            {t('profile.details.name')}
          </Heading>
          <Text data-testid="profile-details-name">{`${userToView?.firstName} ${
            userToView?.lastName ?? ''
          }`}</Text>
        </div>
        <div className="user-details-card__item">
          <Heading label color="medium">
            {t('profile.details.username')}
          </Heading>
          <Text data-testid="profile-details-username">
            {userToView?.userName}
          </Text>
        </div>
        <div className="user-details-card__item">
          <Heading label color="medium">
            {t('profile.details.email')}
          </Heading>
          <Text data-testid="profile-details-email">{userToView?.email}</Text>
        </div>
        <div className="user-details-card__item">
          <Heading label color="medium">
            {t('profile.details.accountId')}
          </Heading>
          <Text data-testid="profile-details-accountId">{userToView?.id}</Text>
        </div>
      </div>
      <div className="user-details-card__actions">
        {!isRootUser && isLoggedInRootOrSuperUser && (
          <div>
            <Button
              disabled={!isLoggedInRootOrSuperUser}
              onClick={() => {
                setIsChangePasswordOpen(!isChangePasswordOpen);
              }}
              data-testid="profile-change-password-button"
            >
              {t('profile.details.change.password.button')}
            </Button>
            <ConditionalRender enable={isChangePasswordOpen}>
              <ChangePasswordDialog
                isOpen={isChangePasswordOpen}
                onClose={setIsChangePasswordOpen}
                user={userToView}
              />
            </ConditionalRender>
          </div>
        )}
        {!isRootUser && (
          <div>
            <TooltipHoverArea
              disabled={isLoggedInRootOrSuperUser}
              tooltip={t('profile.details.disabled.change.email.tooltip')}
              anchor="bottom"
            >
              <Button
                disabled={!isLoggedInRootOrSuperUser}
                onClick={() => {
                  setIsChangeEmailOpen(!isChangeEmailOpen);
                }}
                data-testid="profile-change-email-button"
              >
                {t('profile.details.change.email.button')}
              </Button>
            </TooltipHoverArea>
            <ConditionalRender enable={isChangeEmailOpen}>
              <ChangeEmailDialog
                isOpen={isChangeEmailOpen}
                onClose={setIsChangeEmailOpen}
                user={userToView}
                isViewingPersonalProfile={userToView.id === loggedInUser.id}
              />
            </ConditionalRender>
          </div>
        )}
      </div>
    </Card>
  );
};

export default UserDetailsCard;
